<template>
  <RequestWizardStep name="Record Details" :complete="isComplete">
    <ContentCard title="Now tell us about the records you need.">
      <template v-if="!is3rdParty">
        <LocalScope
          :define="{ releaseAllRecordsLabel: 'Tell us which records to release' }"
          v-slot="{ releaseAllRecordsLabel }"
        >
          <div>
            <p class="form-label">{{ releaseAllRecordsLabel }}:</p>

            <RadioButtons
              :aria-label="releaseAllRecordsLabel"
              :options="[
                { value: true, label: 'Release all records to date'},
                { value: false, label: 'Release records between specific dates:' }
              ]"
              :required="!is3rdParty"
              v-model="releaseAllRecords"
            />

            <LocalScope
              :define="{ startDateLabel: 'Starting Date' }"
              v-slot="{ startDateLabel }"
            >
              <StepSelection v-show-slide="releaseAllRecords === false"
                class="row g-3 ms-3"
              >
                <DatePicker
                  class="col-md-6"
                  :emitType="Date"
                  :label="startDateLabel"
                  :rules="{
                    required: requiredIf(() => releaseAllRecords === false)
                  }"
                  v-model="recordsStartDate"
                  v-model:setFocus="focusOnRecordsStartDate"
                />
                <DatePicker
                  class="col-md-6"
                  :emitType="Date"
                  label="Ending Date"
                  :rules="(releaseAllRecords === false
                    ? {
                      required,
                      isOnOrAfterOther: isOnOrAfterOther(startDateLabel, () => recordsStartDate)
                    }
                    : {})"
                  v-model="recordsEndDate"
                />
              </StepSelection>
            </LocalScope>
          </div>
        </LocalScope>

        <LocalScope
          :define="{ releaseRecordsOnLabel: 'Tell us when to release your records' }"
          v-slot="{ releaseRecordsOnLabel }"
        >
          <div class="row">
            <p class="form-label mt-3">{{ releaseRecordsOnLabel }}:</p>

            <RadioButtons
              :aria-label="releaseRecordsOnLabel"
              :options="[
                { value: 'oneTimeDisclosure', label: 'One time disclosure' },
                { value: 'expireOnDate', label: 'Expiration upon a specified date:' },
                { value: 'expireOnEvent', label: 'Expiration upon specified event:' }
              ]"
              class="col-md-6"
              :required="!is3rdParty"
              v-model="releaseRecordsOn"
            />

            <StepSelection class="col-lg-6 row ms-4 ms-lg-0">
              <div v-show-slide="releaseRecordsOn === 'expireOnDate'">
                <DatePicker
                  :emitType="Date"
                  label="Release Date"
                  :rules="(releaseRecordsOn === 'expireOnDate' ? {
                    required,
                    isAfterToday
                  } : {})"
                  v-model="releaseOnDate"
                  v-model:setFocus="focusOnReleaseOnDate"
                />
              </div>

              <div v-show-slide="releaseRecordsOn === 'expireOnEvent'">
                <TextBox
                  label="Release Event"
                  :rules="{
                    required: requiredIf(() => releaseRecordsOn === 'expireOnEvent'),
                    max: maxLength(50)
                  }"
                  v-model="releaseOnEvent"
                  v-model:setFocus="focusOnReleaseOnEvent"
                />
              </div>
            </StepSelection>
          </div>
        </LocalScope>
      </template>

      <TextArea
        class="mt-4"
        label="Please provide any additional comments or information you may have:"
        rows="3"
        v-model="nonCommonData.recordDetails.comments"
        :rules="{ max: maxLength(2500) }"
      />
    </ContentCard>

    <ContentCard>
      <template #title>
        <h4>
          Please upload
          <template v-if="is3rdParty">
            the required documentation which authorizes you/your
            organization to obtain the requested records.
          </template>
          <template v-else>
            any additional supporting information.
          </template>
        </h4>
      </template>

      <AlertMessage v-if="is3rdParty" type="info" class="mt-4 mb-4">
        <p>The Health Insurance Portability and Accountability Act (HIPAA) Privacy Rule
          (45 C.F.R. § 164.508) prohibits Medicare (a HIPAA covered entity) from disclosing an
          individual's protected health information without a valid authorization.
        </p>

        <h6>
          Required documentation:
        </h6>
        <ul>
          <li>
            <a target="_blank"
              href="https://www.cms.gov/Regulations-and-Guidance/Legislation/FOIA/Downloads/AuthorizationForm.pdf"
            >Medicare Authorization Form</a>
            <PopOver
              class="d-inline"
              placement="top"
              :show-triggers="['click', 'hover']"
              :hide-triggers="['focus', 'hover']"
            >
              <template #reference>
                <IconButton
                  iconClass="bi bi-question-circle"
                  title="More information about Medicare Authorization Form"
                />
              </template>

              A state subpoena is not sufficient. As prescribed by HIPAA, CMS/Medicare will
              not share protected health information without a valid authorization that contains
              the core elements such as name of the beneficiary, signature of the beneficiary,
              date, expiration date of the authorization, purpose, information to be disclosed,
              and name of the person to whom CMS/Medicare may make the requested disclosure.
              In accordance with HIPAA, the completed authorization will enable CMS/Medicare to
              share an individual's personal health information with a third party at the
              individual's request.
            </PopOver>
          </li>
        </ul>

        <h6>
          Additional documentation may include, but is not limited to, the following items:
        </h6>
        <ul>
          <li>Authorized representative confirmation</li>
          <li>Notarized Power of Attorney</li>
          <li>Letters testamentary, letters of administration, successor in interest, next
            of kin, etc.
            <PopOver
              class="d-inline"
              placement="top"
              :show-triggers="['click', 'hover']"
              :hide-triggers="['focus', 'hover']"
            >
              <template #reference>
                <IconButton
                  iconClass="bi bi-question-circle"
                  title="More information about letters testamentary, etc."
                />
              </template>

              Document names may vary by state, but any version submitted must have been signed
              by probate court. We do not accept wills.
            </PopOver>
          </li>
        </ul>
      </AlertMessage>

      <div class="fst-italic text-muted mb-4">
        <span class="bi bi-exclamation-circle"></span>
        <span v-if="is3rdParty"> You are required to upload at least one attachment.</span>
        You can upload multiple files, but
        the cumulative file size cannot exceed 20 MB. Only the following file types are allowed:
        {{ config.allowedFileTypes.map(([ext]) => ext).join(', ') }}.
      </div>

      <FileAttachments v-model="nonCommonData.recordDetails.attachments"
        class="mb-2 mt-4"
        attachButtonClass="btn btn-outline-primary w-100 py-3 mb-3"
        :processNewFile="() => filesProcessing++"
        :rules="{
          required: {
            ...requiredIf(() => is3rdParty),
            $message: 'Please add at least one attachment.'
          }
        }"
        @processing-complete="filesProcessing--"
        @processing-failed="filesProcessing--"
        ref="attachments"
      >
        <template #attach-button-label>
          <span class="bi bi-cloud-arrow-up"></span> Upload documentation...
        </template>
      </FileAttachments>

      <StepSelection v-if="is3rdParty">
        <CheckBox v-model="requiredDocumentationIncluded"
          :required="is3rdParty"
        >
          I certify that this request includes all required documentation
          which authorizes me/my organization to obtain these records.</CheckBox>
      </StepSelection>
    </ContentCard>
  </RequestWizardStep>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import AlertMessage from '@/components/AlertMessage';
import PopOver from '@/components/PopOver';
import IconButton from '@/components/buttons/Icon';
import { DatePicker, TextArea, TextBox } from '@/components/formFields/common';
import FileAttachments from '@/components/formFields/FileAttachments';
import CheckBox from '@/components/formFields/CheckBox';
import RadioButtons from '@/components/formFields/RadioButtons';
import { formatDate } from '@/helpers/dateHelpers';
import RequestWizardStep from '@/components/requestWizard/RequestWizardStep';
import StepSelection from '@/components/requestWizard/StepSelection';
import {
  isOnOrAfterOther, isAfterToday, maxLength, required, requiredIf
} from '@/validators';
import { is3rdParty, nonCommonData } from '../requestWizardState';

export default {
  setup() {
    const v$ = useVuelidate();

    return {
      isAfterToday,
      is3rdParty,
      isOnOrAfterOther,
      maxLength,
      nonCommonData,
      required,
      requiredIf,
      v$
    };
  },

  inject: ['config'],

  components: {
    AlertMessage,
    CheckBox,
    DatePicker,
    FileAttachments,
    IconButton,
    PopOver,
    RadioButtons,
    RequestWizardStep,
    StepSelection,
    TextArea,
    TextBox
  },

  data: () => ({
    filesProcessing: 0,
    focusOnRecordsStartDate: false,
    focusOnReleaseOnDate: false,
    focusOnReleaseOnEvent: false,
    releaseAllRecords: null,
    recordsStartDate: null,
    recordsEndDate: null,
    releaseOnDate: null,
    releaseOnEvent: null,
    releaseRecordsOn: null,
    requiredDocumentationIncluded: false
  }),

  computed: {
    isComplete: ({ v$ }) => !v$.$invalid,

    recordDateRangesVal() {
      return {
        true: 'Release all records to date',
        false: `Release records from ${formatDate(this.recordsStartDate)} to ${formatDate(this.recordsEndDate)}`,
      }[this.releaseAllRecords];
    },

    timelineOfReleaseVal() {
      return this.releaseRecordsOn === 'oneTimeDisclosure'
        ? 'One time disclosure'
        : `Expire on ${{
          expireOnDate: formatDate(this.releaseOnDate),
          expireOnEvent: this.releaseOnEvent
        }[this.releaseRecordsOn]}`;
    }
  },

  watch: {
    recordDateRangesVal(val) { nonCommonData.value.recordDetails.recordDateRanges = val; },

    timelineOfReleaseVal(val) { nonCommonData.value.recordDetails.timelineOfRelease = val; },

    releaseAllRecords(val) {
      setTimeout(() => {
        if (val === false) this.focusOnRecordsStartDate = true;
      }, 500); // Add a delay so calendar control doesn't cover the field.
    },

    releaseRecordsOn(val) {
      setTimeout(() => {
        if (val === 'expireOnDate') this.focusOnReleaseOnDate = true;
        else if (val === 'expireOnEvent') this.focusOnReleaseOnEvent = true;
      }, 500); // Add a delay so calendar control doesn't cover the field.
    },

    requiredDocumentationIncluded(val) {
      if (val) {
        this.$refs.attachments.validate();
      }
    }
  }
};
</script>
